import { STATUS_EMAIL_DEFAULTS, defaultJobMessages, type JobMessages, type StatusEmail, type StatusEmailKind } from '@eduvy/core';
import { useState } from 'react';
import { Icon } from '../components/icons';
import { Button, Checkbox, Field, Input, Section, Select, Textarea } from '../components/ui';

const DELAYS = [
  { hours: 0, label: 'Right away' },
  { hours: 2, label: 'After 2 hours' },
  { hours: 24, label: 'Next day (after 24 hours)' },
  { hours: 48, label: 'After 2 days' },
  { hours: 72, label: 'After 3 days' },
];

/** Who hears about candidates' progress, and what candidates are told when the team decides. */
export function JobMessagesCard({ value, onChange, creatorEmail, n }: { value: JobMessages | undefined; onChange: (m: JobMessages) => void; creatorEmail?: string | undefined; n?: number }) {
  const m = value ?? defaultJobMessages();
  const [notifyText, setNotifyText] = useState(m.notify.join(', '));
  const [notifyError, setNotifyError] = useState<string | null>(null);

  const commitNotify = () => {
    const list = notifyText
      .split(/[,;\s]+/)
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    const bad = list.filter((e) => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e));
    setNotifyError(bad.length ? `Not an email address: ${bad.join(', ')}` : list.length > 10 ? 'At most 10 people.' : null);
    if (!bad.length) onChange({ ...m, notify: [...new Set(list)].slice(0, 10) });
  };

  return (
    <Section id="emails" n={n} title="Emails" description="Who on your side hears about progress, and what candidates are told when you decide.">
      <div className="space-y-6">
        <Field
          label="Tell these people about candidates' progress"
          hint={`Emailed when a candidate finishes the skills assessment, or books, moves or cancels a recruiter interview (with a calendar invite). Leave empty to email ${creatorEmail ? creatorEmail : 'whoever created the job'}.`}
          error={notifyError}
        >
          <Input value={notifyText} leading={<Icon name="mail" className="h-4 w-4" />} placeholder="recruiter@company.com, hiring.manager@company.com" onChange={(e) => setNotifyText(e.target.value)} onBlur={commitNotify} />
        </Field>
        <div className="grid gap-4 xl:grid-cols-2">
          <StatusEmailFields
            kind="reject"
            title="Rejection email to the candidate"
            blurb="Sent when you reject a candidate, if the box is ticked in the reject dialog. A delay feels less automatic; changing your decision before it goes out cancels it."
            value={m.reject}
            onChange={(reject) => onChange({ ...m, reject })}
          />
          <StatusEmailFields
            kind="select"
            title="Selection email to the candidate"
            blurb="Sent when you select a candidate, if the box is ticked. Your team still sends the offer itself."
            value={m.select}
            onChange={(select) => onChange({ ...m, select })}
          />
        </div>
      </div>
    </Section>
  );
}

function StatusEmailFields({ kind, title, blurb, value, onChange }: { kind: StatusEmailKind; title: string; blurb: string; value: StatusEmail; onChange: (v: StatusEmail) => void }) {
  const d = STATUS_EMAIL_DEFAULTS[kind];
  const [open, setOpen] = useState(Boolean(value.subject || value.body));
  return (
    <div className="rounded-md bg-surface-2 p-4 ring-1 ring-border">
      <div className="flex items-start gap-3">
        <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] bg-muted text-muted-fg">
          <Icon name={kind === 'reject' ? 'x' : 'star'} className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-fg">{title}</p>
          <p className="mt-0.5 text-xs leading-relaxed text-muted-fg">{blurb}</p>
        </div>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <Checkbox checked={value.send} onChange={(send) => onChange({ ...value, send })} className="sm:pt-7">
          Ticked by default
        </Checkbox>
        <Field label="Send">
          <Select value={value.delayHours} onChange={(e) => onChange({ ...value, delayHours: Number(e.target.value) })}>
            {[...new Set([...DELAYS.map((x) => x.hours), value.delayHours])].map((h) => (
              <option key={h} value={h}>
                {DELAYS.find((x) => x.hours === h)?.label ?? `After ${h} hours`}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      {open ? (
        <div className="mt-4 space-y-3">
          <Field label="Subject" hint="Leave empty for the default.">
            <Input value={value.subject ?? ''} placeholder={d.subject} onChange={(e) => onChange({ ...value, subject: e.target.value || undefined })} />
          </Field>
          <Field label="Message" hint="{first_name}, {job_title} and {company} are filled in. Leave empty for the default shown.">
            <Textarea rows={9} value={value.body ?? ''} placeholder={d.body} onChange={(e) => onChange({ ...value, body: e.target.value || undefined })} />
          </Field>
        </div>
      ) : (
        <Button size="xs" variant="soft" icon="pen" className="mt-4" onClick={() => setOpen(true)}>
          See or change the wording
        </Button>
      )}
    </div>
  );
}
